
import { Component, Prop, Vue } from 'vue-property-decorator';
import VTable from '../shared/table/VTable.vue';
import VColumnSelect from '@/components/shared/table/VColumnSelect.vue';
import SalesOverviewFilter from '@/components/salesOverview/SalesOverviewFilter.vue';
import SalesOverviewLoader from '@/components/salesOverview/SalesOverviewLoader.vue';
import { SalesOverview } from '@/interfaces/models/SalesOverview';
import { ColumnHeader } from '@/interfaces/components/ColumnHeader';
import { VCardText, VSimpleTable } from 'vuetify/lib';
import salesOverviewHeaders from './headers';
import Sale from '@/interfaces/models/Sale';

@Component({
  components: { VTable, VSimpleTable, VColumnSelect, SalesOverviewFilter, SalesOverviewLoader },
})
export default class SalesOverviewTable extends Vue {
  @Prop({ type: Array, required: true }) public items!: SalesOverview[];
  @Prop({ type: Number, required: true }) public total!: number;
  @Prop({ type: Boolean, default: false }) public showFoodspot!: boolean;

  public $refs!: {
    filter: InstanceType<typeof SalesOverviewFilter> & {
      validateFilter: any;
    };
  };

  public sourceHeaders = this.accessibleHeaders;
  public headers: ColumnHeader[] = this.accessibleHeaders.filter((c: ColumnHeader) => c.default);
  public sortedByTotalAmount: 'asc' | 'desc' | null = null;

  get itemsSorted(): SalesOverview[] {
    if (!this.sortedByTotalAmount) {
      return [...this.items].sort((a, b) => a.venue.name.localeCompare(b.venue.name));
    }

    if (this.sortedByTotalAmount === 'asc') {
      return [...this.items].sort(
        (a, b) => parseFloat(a.totalAmount.$numberDecimal) - parseFloat(b.totalAmount.$numberDecimal),
      );
    } else {
      return [...this.items].sort(
        (a, b) => parseFloat(b.totalAmount.$numberDecimal) - parseFloat(a.totalAmount.$numberDecimal),
      );
    }
  }

  get accessibleHeaders() {
    return salesOverviewHeaders.filter((c: ColumnHeader) => {
      if (c.value === 'foodspot' && !this.showFoodspot) {
        return false;
      }

      return true;
    });
  }

  public getTotalOrders(sales: Sale[], provider: string) {
    const sale = sales.find((s: Sale) => s.provider === provider);

    return sale ? sale.totalOrders : '-';
  }

  public getTotalAmount(sales: Sale[], provider: string) {
    const sale = sales.find((s: Sale) => s.provider === provider);

    return sale ? `€${parseFloat(sale.totalAmount.$numberDecimal).toFixed(2)}` : '-';
  }

  public getImageDimensions(
    value:
      | 'wolt'
      | 'jet'
      | 'uber_eats'
      | 'terminal'
      | 'terminal_takeaway'
      | 'terminal_inside'
      | 'web_app'
      | 'pickup'
      | 'delivery'
      | 'inside'
      | 'foodspot'
      | 'orderAtTable',
  ) {
    const dimensions = {
      wolt: { width: 30, height: 20 },
      jet: { width: 60, height: 20 },
      uber_eats: { width: 50, height: 20 },
      terminal: { width: 20, height: 20 },
      terminal_takeaway: { width: 20, height: 20 },
      terminal_inside: { width: 20, height: 20 },
      web_app: { width: 20, height: 20 },
      pickup: { width: 20, height: 20 },
      delivery: { width: 20, height: 20 },
      inside: { width: 20, height: 20 },
      foodspot: { width: 20, height: 20 },
      orderAtTable: { width: 20, height: 20 },
    };

    return dimensions[value] || { width: 40, height: 20 };
  }

  public getTotalOrdersByProvider(provider: string) {
    let totalOrders = 0;

    this.itemsSorted.forEach((venue) => {
      venue.sales.forEach((sale) => {
        if (sale.provider === provider) {
          totalOrders += sale.totalOrders;
        }
      });
    });

    return totalOrders;
  }

  public getTotalAmountByProvider(provider: string) {
    let totalAmount = 0;

    this.itemsSorted.forEach((venue) => {
      venue.sales.forEach((sale) => {
        if (sale.provider === provider) {
          totalAmount += parseFloat(sale.totalAmount.$numberDecimal);
        }
      });
    });

    return `€${totalAmount.toFixed(2)}`;
  }

  public async onExport() {
    const results = await this.$refs.filter.validateFilter();

    if (results.result) {
      this.$emit('export', results.data);
    }
  }

  public onSearch(queries: { customerGroup: string; from: string; to: string }) {
    this.$emit('search', queries);
  }

  public sortByTotalAmount() {
    this.sortedByTotalAmount = !this.sortedByTotalAmount ? 'desc' : this.sortedByTotalAmount === 'desc' ? 'asc' : null;
  }
}
